/**
 * @sw-package b2b
 */

import Plugin from 'src/plugin-system/plugin.class';
import { asRecord } from '../../helper/record.helper';

export type Snippets = {
    historyChangeFallback?: string,
    historyDiscountAbsolute?: string,
    historyDiscountRelative?: string,
    historyOrderCreated?: string,
    historySentQuoteValue?: string,
    merchantComment?: string,
    customerComment?: string,
    quoteMessageSendError?: string,
    quoteHistoryMissingUrlError?: string,
    quoteHistoryLoadError?: string,
    quoteHistoryReadError?: string,
};

export type QuoteHistoryItemOptions = {
    currencyIsoCode?: string,
    dateTimeLocale?: string,
    dateTimeTimeZone?: string,
    quoteSummary?: QuoteSummary | null,
    statusLabels?: Record<string, string>,
    unreadHistoryIds?: string[],
    historyItemBagSelector?: string,
    snippets?: Partial<Snippets>,
};

export type HistoryEntry = Record<string, unknown>;

export const SOURCE_HISTORY_IDS_FIELD = '_sourceHistoryIds';

type QuoteSummary = {
    sentStatuses?: string[],
    orderNumber?: string,
    orderUrl?: string,
    statusChangeLabels?: {
        added?: string,
        sent?: string,
        withdraw?: string,
    },
    items?: QuoteSummaryItem[],
};

type QuoteSummaryItem = {
    label?: string,
    value?: string,
    valuePath?: string,
    formatter?: 'currency' | 'datetime' | 'discount' | 'label',
};

type HistoryActor = {
    name: string,
    initials: string,
    isCustomer: boolean,
};

const DEFAULT_SNIPPETS: Required<Snippets> = {
    historyChangeFallback: 'Updated',
    historyDiscountAbsolute: 'Absolute %value%',
    historyDiscountRelative: 'Relative %value%%',
    historyOrderCreated: 'Order created',
    historySentQuoteValue: 'Quote #%quoteNumber%',
    merchantComment: 'Merchant',
    customerComment: 'You',
};

const HISTORY_MERGE_WINDOW_MS = 15 * 1000;

export default class B2bQuoteHistoryItemPlugin extends Plugin {
    static options: QuoteHistoryItemOptions = {
        currencyIsoCode: 'EUR',
        dateTimeLocale: undefined,
        dateTimeTimeZone: undefined,
        quoteSummary: null,
        statusLabels: {},
        unreadHistoryIds: [],
        historyItemBagSelector: '[data-quote-history-item-bag]',
        snippets: DEFAULT_SNIPPETS,
    };

    private el: HTMLElement;

    private renderOptions: QuoteHistoryItemOptions = B2bQuoteHistoryItemPlugin.options;

    init(): void {
        this.renderOptions = this.options;
    }

    public setRenderOptions(options: QuoteHistoryItemOptions): void {
        this.renderOptions = {
            ...this.renderOptions,
            ...options,
            statusLabels: {
                ...this.renderOptions.statusLabels,
                ...options.statusLabels,
            },
            snippets: {
                ...this.renderOptions.snippets,
                ...options.snippets,
            },
        };
    }

    public render(entry: HistoryEntry, previousEntry?: HistoryEntry, nextEntry?: HistoryEntry): HTMLElement[] {
        const status = this.getStatusChange(entry);
        const hasDetailContent = this.hasDetailChanges(entry)
            || this.isQuoteSummaryStatusChange(entry)
            || Boolean(this.getComment(entry));

        if (status && this.shouldRenderMerchantSentArticle(entry, status, nextEntry)) {
            const detailEntry = this.renderMerchantSentHistoryEntry(entry);

            return [
                this.renderStatusHistoryEntry(entry, status, !detailEntry),
                detailEntry,
            ].filter((node): node is HTMLElement => node !== null);
        }

        if (status && hasDetailContent) {
            const detailEntry = this.renderDetailHistoryEntry(entry, previousEntry);
            const statusEntry = this.renderStatusHistoryEntry(entry, status, false);
            const historyEntries = this.isWithdrawStatus(status)
                ? [detailEntry, statusEntry]
                : [statusEntry, detailEntry];

            return historyEntries.filter((node): node is HTMLElement => node !== null);
        }

        if (status) {
            const statusEntry = this.renderStatusHistoryEntry(entry, status);

            return statusEntry ? [statusEntry] : [];
        }

        const detailEntry = this.renderDetailHistoryEntry(entry, previousEntry);

        return detailEntry ? [detailEntry] : [];
    }

    public mergeMerchantHistories(histories: HistoryEntry[]): HistoryEntry[] {
        const entries = histories.map((entry) => ({ ...entry }));
        const mergedIndexes = new Set<number>();

        this.mergeMerchantDetailChangeHistories(entries, mergedIndexes);
        this.mergeMerchantCommentHistories(entries, mergedIndexes);
        this.mergeAddedStatusCommentHistories(entries, mergedIndexes);

        return entries.filter((_, index) => !mergedIndexes.has(index));
    }

    private mergeMerchantDetailChangeHistories(entries: HistoryEntry[], mergedIndexes: Set<number>): void {
        entries.forEach((entry, index) => {
            if (mergedIndexes.has(index) || !this.isMergeableMerchantDetailChangeHistory(entry)) {
                return;
            }

            for (let sourceIndex = index + 1; sourceIndex < entries.length; sourceIndex += 1) {
                if (mergedIndexes.has(sourceIndex)) {
                    continue;
                }

                const source = entries[sourceIndex];

                if (!this.canMergeMerchantDetailChangeEntries(entries[index], source)) {
                    continue;
                }

                entries[index] = this.mergeChangesIntoHistoryEntry(entries[index], source);
                mergedIndexes.add(sourceIndex);
            }
        });
    }

    private mergeMerchantCommentHistories(entries: HistoryEntry[], mergedIndexes: Set<number>): void {
        entries.forEach((entry, index) => {
            if (mergedIndexes.has(index)) {
                return;
            }

            const targetIndex = this.findMerchantCommentMergeTargetIndex(entries, index, mergedIndexes);

            if (targetIndex === null) {
                return;
            }

            const target = entries[targetIndex];

            entries[targetIndex] = this.mergeCommentIntoHistoryEntry(target, entry);
            mergedIndexes.add(index);
        });
    }

    private findMerchantCommentMergeTargetIndex(
        entries: HistoryEntry[],
        commentIndex: number,
        mergedIndexes: Set<number>,
    ): number | null {
        const commentEntry = entries[commentIndex];

        if (!this.isMerchantCommentOnlyEntry(commentEntry)) {
            return null;
        }

        const sentAt = this.getMerchantMergeTimestamp(commentEntry);

        if (sentAt === null) {
            return null;
        }

        const searchPlans: Array<[number, number, 1 | -1, (entry: HistoryEntry) => boolean]> = [
            [commentIndex + 1, entries.length, 1, (entry) => this.isMerchantDetailChangeHistory(entry)],
            [commentIndex + 1, entries.length, 1, (entry) => this.isMerchantMergeTarget(entry)],
            [commentIndex - 1, -1, -1, (entry) => this.isMerchantDetailChangeHistory(entry)],
            [commentIndex - 1, -1, -1, (entry) => this.isMerchantMergeTarget(entry)],
        ];

        for (const [startIndex, endIndex, step, matcher] of searchPlans) {
            const targetIndex = this.findMerchantMergeTargetIndex(
                entries,
                sentAt,
                startIndex,
                endIndex,
                step,
                mergedIndexes,
                matcher,
            );

            if (targetIndex !== null) {
                return targetIndex;
            }
        }

        return null;
    }

    private findMerchantMergeTargetIndex(
        entries: HistoryEntry[],
        sentAt: number,
        startIndex: number,
        endIndex: number,
        step: 1 | -1,
        mergedIndexes: Set<number>,
        matcher: (entry: HistoryEntry) => boolean,
    ): number | null {
        for (let index = startIndex; index !== endIndex; index += step) {
            if (mergedIndexes.has(index)) {
                continue;
            }

            const entry = entries[index];

            if (
                this.isWithinMergeWindow(this.getMerchantMergeTimestamp(entry), sentAt)
                && matcher(entry)
                && !this.getComment(entry)
            ) {
                return index;
            }
        }

        return null;
    }

    private mergeAddedStatusCommentHistories(entries: HistoryEntry[], mergedIndexes: Set<number>): void {
        const targetIndexes: number[] = [];

        entries.forEach((entry, index) => {
            if (mergedIndexes.has(index)) {
                return;
            }

            if (this.getCreatedAtMergeTimestamp(entry) !== null && this.isAddedStatusHistory(entry)) {
                targetIndexes.push(index);
            }
        });

        entries.forEach((entry, index) => {
            if (mergedIndexes.has(index)) {
                return;
            }

            const targetIndex = this.findAddedStatusCommentMergeTargetIndex(
                entries,
                index,
                targetIndexes,
                mergedIndexes,
            );

            if (targetIndex === null) {
                return;
            }

            const target = entries[targetIndex];

            if (this.getComment(target)) {
                return;
            }

            entries[targetIndex] = this.mergeCommentIntoHistoryEntry(target, entry);
            mergedIndexes.add(index);
        });
    }

    private findAddedStatusCommentMergeTargetIndex(
        entries: HistoryEntry[],
        commentIndex: number,
        targetIndexes: number[],
        mergedIndexes: Set<number>,
    ): number | null {
        const commentEntry = entries[commentIndex];

        if (!this.isRequestCommentOnlyEntry(commentEntry)) {
            return null;
        }

        const commentCreatedAt = this.getCreatedAtMergeTimestamp(commentEntry);

        if (commentCreatedAt === null) {
            return null;
        }

        return targetIndexes.reduce<{ index: number | null, distance: number }>((closestTarget, targetIndex) => {
            if (targetIndex === commentIndex || mergedIndexes.has(targetIndex)) {
                return closestTarget;
            }

            const target = entries[targetIndex];
            const targetCreatedAt = this.getCreatedAtMergeTimestamp(target);

            if (targetCreatedAt === null || this.getComment(target)) {
                return closestTarget;
            }

            const distance = Math.abs(targetCreatedAt - commentCreatedAt);

            if (distance > HISTORY_MERGE_WINDOW_MS || distance >= closestTarget.distance) {
                return closestTarget;
            }

            return { index: targetIndex, distance };
        }, { index: null, distance: Number.POSITIVE_INFINITY }).index;
    }

    private canMergeMerchantDetailChangeEntries(target: HistoryEntry, source: HistoryEntry): boolean {
        return this.isMergeableMerchantDetailChangeHistory(source)
            && this.isWithinMergeWindow(
                this.getMerchantMergeTimestamp(target),
                this.getMerchantMergeTimestamp(source),
            )
            && !this.hasOverlappingDisplayChangeFields(target, source);
    }

    private isMergeableMerchantDetailChangeHistory(entry: HistoryEntry): boolean {
        return this.isMerchantDetailChangeHistory(entry) && !this.getComment(entry);
    }

    private hasOverlappingDisplayChangeFields(target: HistoryEntry, source: HistoryEntry): boolean {
        const targetFields = new Set(this.getDisplayChangeFields(target));

        return this.getDisplayChangeFields(source).some((field) => targetFields.has(field));
    }

    private getDisplayChangeFields(entry: HistoryEntry): string[] {
        const changes = asRecord(entry.changes);
        const newChanges = asRecord(changes.new);

        return Object.keys(newChanges).filter((field) => !['stateId', 'displayState', 'storefrontState'].includes(field));
    }

    private mergeChangesIntoHistoryEntry(target: HistoryEntry, source: HistoryEntry): HistoryEntry {
        const targetChanges = asRecord(target.changes);
        const sourceChanges = asRecord(source.changes);
        const targetOldChanges = asRecord(targetChanges.old);
        const sourceOldChanges = asRecord(sourceChanges.old);
        const targetNewChanges = asRecord(targetChanges.new);
        const sourceNewChanges = asRecord(sourceChanges.new);

        return {
            ...target,
            updatedAt: target.updatedAt ?? source.updatedAt,
            createdById: target.createdById ?? source.createdById,
            createdBy: target.createdBy ?? source.createdBy,
            changes: {
                ...targetChanges,
                ...sourceChanges,
                old: {
                    ...targetOldChanges,
                    ...sourceOldChanges,
                },
                new: {
                    ...targetNewChanges,
                    ...sourceNewChanges,
                },
            },
            [SOURCE_HISTORY_IDS_FIELD]: Array.from(new Set([
                ...this.getSourceHistoryIds(target),
                ...this.getSourceHistoryIds(source),
            ])),
        };
    }

    private mergeCommentIntoHistoryEntry(target: HistoryEntry, commentEntry: HistoryEntry): HistoryEntry {
        return {
            ...target,
            comment: commentEntry.comment,
            commentId: target.commentId ?? commentEntry.commentId,
            commentVersionId: target.commentVersionId ?? commentEntry.commentVersionId,
            customerId: target.customerId ?? commentEntry.customerId,
            customer: target.customer ?? commentEntry.customer,
            employeeId: target.employeeId ?? commentEntry.employeeId,
            employee: target.employee ?? commentEntry.employee,
            createdById: target.createdById ?? commentEntry.createdById,
            createdBy: target.createdBy ?? commentEntry.createdBy,
            [SOURCE_HISTORY_IDS_FIELD]: Array.from(new Set([
                ...this.getSourceHistoryIds(target),
                ...this.getSourceHistoryIds(commentEntry),
            ])),
        };
    }

    private renderDetailHistoryEntry(entry: HistoryEntry, previousEntry?: HistoryEntry): HTMLElement | null {
        const actor = this.getActor(entry);
        const createdAt = this.formatDate(this.getHistoryDate(entry));
        const changeItems = this.renderChangeItems(entry, previousEntry);
        const comment = this.renderComment(entry);
        const actionFallback = changeItems || comment ? null : this.renderActionFallback(entry);
        const body = [changeItems, comment, actionFallback].filter((node): node is HTMLElement => node !== null);

        if (body.length === 0) {
            return null;
        }

        const item = this.renderItemElement(actor, createdAt, body);

        if (this.shouldMoveMerchantSentSummaryToNextChange(previousEntry, entry)) {
            item?.classList.add('quote-detail-history__item--merchant-sent');
        }

        this.markUnreadHistoryElement(item, entry);

        return item;
    }

    private renderMerchantSentHistoryEntry(entry: HistoryEntry): HTMLElement | null {
        const actor = this.getMerchantActor();
        const sentAt = this.formatDate(this.getHistoryDate(entry));
        const quoteSummaryItems = this.renderQuoteSummaryItems(entry);
        const comment = this.renderComment(entry);
        const body = [quoteSummaryItems, comment].filter((node): node is HTMLElement => node !== null);

        if (body.length === 0) {
            return null;
        }

        const item = this.renderItemElement(actor, sentAt, body);

        item?.classList.add('quote-detail-history__item--merchant-sent');
        this.markUnreadHistoryElement(item, entry);

        return item;
    }

    private renderItemElement(
        actor: HistoryActor,
        createdAt: string,
        body: HTMLElement[],
    ): HTMLElement | null {
        const item = this.cloneTemplate('[data-quote-history-item-template]');

        if (!item) {
            return null;
        }

        const avatar = item.querySelector<HTMLElement>('[data-quote-history-actor-initials]');
        const actorName = item.querySelector<HTMLElement>('[data-quote-history-actor-name]');
        const createdAtElement = item.querySelector<HTMLElement>('[data-quote-history-created-at]');
        const content = item.querySelector<HTMLElement>('[data-quote-history-item-content]');

        if (avatar) {
            avatar.textContent = actor.initials;
            avatar.classList.toggle('quote-detail-history__avatar--customer', actor.isCustomer);
        }

        if (actorName) {
            actorName.textContent = actor.name;
        }

        if (createdAtElement) {
            createdAtElement.textContent = createdAt;
        }

        content?.replaceChildren(...body);

        return item;
    }

    private renderStatusHistoryEntry(entry: HistoryEntry, status: string, markAsUnread = true): HTMLElement | null {
        const statusEntry = this.cloneTemplate('[data-quote-history-status-template]');

        if (!statusEntry) {
            return null;
        }

        const sentAt = this.formatDate(this.getHistoryDate(entry));
        const state = this.formatValue(status);
        const statusBadge = statusEntry.querySelector<HTMLElement>('[data-status]');
        const sentAtElement = statusEntry.querySelector<HTMLElement>('[data-quote-history-created-at]');

        if (statusBadge) {
            statusBadge.className = [
                'badge',
                'badge-lg',
                'quote-item-status-badge',
                `quote-item-status-badge-${state}`,
            ].join(' ');
            statusBadge.dataset.status = state;
            statusBadge.textContent = this.getStatusLabel(state);
        }

        if (sentAtElement) {
            sentAtElement.textContent = sentAt;
        }

        if (markAsUnread) {
            this.markUnreadHistoryElement(statusEntry, entry);
        }

        return statusEntry;
    }

    private markUnreadHistoryElement(element: HTMLElement | null, entry: HistoryEntry): void {
        if (!element || !this.isUnreadMerchantHistory(entry)) {
            return;
        }

        const historyId = this.getRepresentativeHistoryId(entry);

        if (!historyId) {
            return;
        }

        element.dataset.historyId = historyId;
        element.dataset.unseen = 'true';
        element.querySelector<HTMLElement>(this.renderOptions.historyItemBagSelector)?.classList.remove('d-none');
    }

    private isUnreadMerchantHistory(entry: HistoryEntry): boolean {
        const unreadHistoryIds = this.renderOptions.unreadHistoryIds;

        return Array.isArray(unreadHistoryIds)
            && this.getSourceHistoryIds(entry).some((historyId) => unreadHistoryIds.includes(historyId));
    }

    private getSourceHistoryIds(entry: HistoryEntry): string[] {
        const sourceHistoryIds = entry[SOURCE_HISTORY_IDS_FIELD];

        if (Array.isArray(sourceHistoryIds)) {
            return sourceHistoryIds.filter((historyId): historyId is string => typeof historyId === 'string');
        }

        const historyId = this.getRepresentativeHistoryId(entry);

        return historyId ? [historyId] : [];
    }

    private getRepresentativeHistoryId(entry: HistoryEntry): string {
        return typeof entry.id === 'string' ? entry.id : '';
    }

    private renderQuoteSummaryItems(entry: HistoryEntry): HTMLElement | null {
        const items = this.renderOptions.quoteSummary?.items ?? [];
        const summaryItems = items
            .map((item) => {
                if (!this.shouldRenderSummaryItem(item, entry)) {
                    return null;
                }

                const value = this.resolveSummaryItemValue(item, entry);

                if (!item.label || !value) {
                    return null;
                }

                return this.renderSummaryChangeItem(item.label, value);
            })
            .filter((node): node is HTMLElement => node !== null);

        if (summaryItems.length === 0) {
            return null;
        }

        return this.renderChangesContainer(summaryItems);
    }

    private shouldRenderSummaryItem(item: QuoteSummaryItem, entry: HistoryEntry): boolean {
        if (item.formatter !== 'discount') {
            return true;
        }

        const field = item.valuePath ? this.getChangeFieldFromValuePath(item.valuePath) : '';

        return field !== 'discount' || this.hasNewChangeField(entry, field);
    }

    private resolveSummaryItemValue(item: QuoteSummaryItem, entry: HistoryEntry): string | null {
        if (item.valuePath) {
            const value = this.getValueByPath(entry, item.valuePath);

            if (value === null || value === undefined) {
                return null;
            }

            return this.formatConfiguredValue(value, item.formatter) || null;
        }

        return this.formatConfiguredValue(item.value, item.formatter) || this.formatValue(item.value) || null;
    }

    private renderSummaryChangeItem(label: string | null, value: string, valueUrl = ''): HTMLElement {
        const item = document.createElement('span');
        item.className = 'quote-detail-history__change';

        if (label) {
            const labelElement = document.createElement('span');
            labelElement.className = 'quote-detail-history__change-label';
            labelElement.textContent = label;
            item.append(labelElement);
        }

        const valueElement = document.createElement('span');
        valueElement.className = 'quote-detail-history__change-value';

        if (valueUrl) {
            const linkElement = document.createElement('a');
            linkElement.className = 'value col-6 quote-order-number';
            linkElement.textContent = `${value}->`;
            linkElement.href = valueUrl;
            valueElement.append(linkElement);
        } else {
            valueElement.textContent = value;
        }

        item.append(valueElement);

        return item;
    }

    private renderChangeItems(entry: HistoryEntry, previousEntry?: HistoryEntry): HTMLElement | null {
        if (this.isQuoteSummaryStatusChange(entry)) {
            return this.renderQuoteSummaryStatusChange(entry);
        }

        const changes = asRecord(entry.changes);
        const newChanges = asRecord(changes.new);
        const configuredChangeItems = Object.entries(newChanges)
            .filter(([field]) => !['stateId', 'displayState', 'storefrontState'].includes(field))
            .map(([field, value]) => {
                const configuredItem = this.getSummaryItemByChangeField(field);
                const formattedLabel = this.formatChangeLabel(field, configuredItem);
                const formattedValue = this.formatHistoryChangeValue(field, value, configuredItem, entry);

                if (!formattedValue) {
                    return null;
                }

                return this.renderSummaryChangeItem(
                    formattedLabel,
                    formattedValue,
                    this.getHistoryChangeValueUrl(field),
                );
            });
        const changeItems = [
            this.renderMerchantSentSummaryChangeItem(entry, previousEntry),
            ...configuredChangeItems,
        ].filter((node): node is HTMLElement => node !== null);

        if (changeItems.length === 0) {
            return null;
        }

        return this.renderChangesContainer(changeItems);
    }

    private renderMerchantSentSummaryChangeItem(entry: HistoryEntry, previousEntry?: HistoryEntry): HTMLElement | null {
        if (!this.shouldMoveMerchantSentSummaryToNextChange(previousEntry, entry)) {
            return null;
        }

        const quoteSummaryItem = this.renderOptions.quoteSummary?.items?.[0];
        const value = quoteSummaryItem ? this.resolveSummaryItemValue(quoteSummaryItem, entry) : '';

        if (!quoteSummaryItem?.label || !value) {
            return null;
        }

        return this.renderSummaryChangeItem(quoteSummaryItem.label, value);
    }

    private formatChangeLabel(field: string, configuredItem?: QuoteSummaryItem): string {
        if (field === 'orderId') {
            return this.getSnippet('historyOrderCreated');
        }

        return configuredItem?.label ?? this.formatLabel(field);
    }

    private getHistoryChangeValueUrl(field: string): string {
        if (field === 'orderId') {
            return this.formatValue(this.renderOptions.quoteSummary?.orderUrl);
        }

        return '';
    }

    private formatHistoryChangeValue(
        field: string,
        value: unknown,
        configuredItem: QuoteSummaryItem | undefined,
        entry: HistoryEntry,
    ): string {
        if (field === 'orderId') {
            return this.formatValue(this.renderOptions.quoteSummary?.orderNumber)
                || this.formatChangeValue(field, value);
        }

        return configuredItem?.valuePath
            ? this.resolveSummaryItemValue(configuredItem, entry)
            : this.formatChangeValue(field, value);
    }

    private renderQuoteSummaryStatusChange(entry: HistoryEntry): HTMLElement | null {
        const value = this.getQuoteSummaryStatusChangeValue(entry);

        if (!value) {
            return null;
        }

        return this.renderChangesContainer([
            this.renderSummaryChangeItem(this.getQuoteSummaryStatusChangeLabel(entry), value),
        ]);
    }

    private renderChangesContainer(changeItems: HTMLElement[]): HTMLElement {
        const container = document.createElement('div');
        container.className = 'quote-detail-history__changes';
        container.replaceChildren(...changeItems);

        return container;
    }

    private getStatusChange(entry: HistoryEntry): string {
        const changes = asRecord(entry.changes);
        const newChanges = asRecord(changes.new);

        return this.getStorefrontState(newChanges);
    }

    private getStorefrontState(changes: Record<string, unknown>): string {
        const displayState = asRecord(changes.displayState);

        return this.formatValue(displayState.storefrontState ?? changes.storefrontState);
    }

    private isQuoteSummaryStatusChange(entry: HistoryEntry): boolean {
        const changes = asRecord(entry.changes);
        const newChanges = asRecord(changes.new);
        const newStatus = this.getStorefrontState(newChanges);

        if (this.isWithdrawStatus(newStatus)) {
            return true;
        }

        return this.isDraftToRequestSentStatusChange(entry);
    }

    private isDraftToRequestSentStatusChange(entry: HistoryEntry): boolean {
        const changes = asRecord(entry.changes);
        const oldChanges = asRecord(changes.old);
        const newChanges = asRecord(changes.new);

        return this.getStorefrontState(oldChanges) === 'draft'
            && this.getStorefrontState(newChanges) === 'request_sent';
    }

    private hasDetailChanges(entry: HistoryEntry): boolean {
        const changes = asRecord(entry.changes);
        const newChanges = asRecord(changes.new);

        return Object.entries(newChanges).some(([field, value]) => {
            return !['stateId', 'displayState', 'storefrontState'].includes(field) && this.formatChangeValue(field, value);
        });
    }

    private hasNewChangeField(entry: HistoryEntry, field: string): boolean {
        const changes = asRecord(entry.changes);
        const newChanges = asRecord(changes.new);

        return Object.prototype.hasOwnProperty.call(newChanges, field);
    }

    private hasHistoryChanges(entry: HistoryEntry): boolean {
        const changes = asRecord(entry.changes);
        const oldChanges = asRecord(changes.old);
        const newChanges = asRecord(changes.new);

        return Object.keys(oldChanges).length > 0 || Object.keys(newChanges).length > 0;
    }

    private shouldRenderMerchantSentArticle(entry: HistoryEntry, status: string, nextEntry?: HistoryEntry): boolean {
        return !this.shouldMoveMerchantSentSummaryToNextChange(entry, nextEntry)
            && this.isMerchantSentStatus(status)
            && this.isMerchantChangeHistory(entry);
    }

    private shouldMoveMerchantSentSummaryToNextChange(
        sentEntry: HistoryEntry | undefined,
        nextEntry: HistoryEntry | undefined,
    ): boolean {
        if (!sentEntry || !nextEntry) {
            return false;
        }

        return this.isWithinMergeWindow(
            this.getMerchantMergeTimestamp(sentEntry),
            this.getMerchantMergeTimestamp(nextEntry),
        )
            && this.isOpenOfferStatusHistory(sentEntry)
            && this.isMerchantDetailChangeHistory(nextEntry);
    }

    private isMerchantChangeHistory(entry: HistoryEntry): boolean {
        return !this.isCustomerOrEmployeeHistory(entry) && this.hasHistoryChanges(entry);
    }

    private isMerchantSentStatusHistory(entry: HistoryEntry): boolean {
        const status = this.getStatusChange(entry);

        return Boolean(status)
            && this.isMerchantSentStatus(status)
            && this.isMerchantChangeHistory(entry);
    }

    private isOpenOfferStatusHistory(entry: HistoryEntry): boolean {
        return this.getStatusChange(entry) === 'open_offer'
            && this.isMerchantChangeHistory(entry);
    }

    private isMerchantDetailChangeHistory(entry: HistoryEntry): boolean {
        return !this.getStatusChange(entry)
            && !this.isCustomerOrEmployeeHistory(entry)
            && this.hasDetailChanges(entry);
    }

    private isMerchantMergeTarget(entry: HistoryEntry): boolean {
        const status = this.getStatusChange(entry);

        if (status && this.isMerchantSentStatus(status)) {
            return true;
        }

        return !this.isCustomerOrEmployeeHistory(entry) && this.hasDetailChanges(entry);
    }

    private isMerchantCommentOnlyEntry(entry: HistoryEntry): boolean {
        return this.isCommentOnlyEntry(entry) && !this.isCustomerOrEmployeeHistory(entry);
    }

    private isCommentOnlyEntry(entry: HistoryEntry): boolean {
        return Boolean(this.getComment(entry))
            && !this.getStatusChange(entry)
            && !this.hasDetailChanges(entry);
    }

    private isRequestCommentOnlyEntry(entry: HistoryEntry): boolean {
        return this.formatValue(entry.action) === 'request'
            && this.isCommentOnlyEntry(entry);
    }

    private isAddedStatusHistory(entry: HistoryEntry): boolean {
        return this.formatValue(entry.action) === 'state_change'
            && this.isDraftToRequestSentStatusChange(entry);
    }

    private isMerchantSentStatus(status: string): boolean {
        const sentStatuses = this.renderOptions.quoteSummary?.sentStatuses ?? [];
        const normalizedStatus = status.toLowerCase();

        return sentStatuses.some((sentStatus) => sentStatus.toLowerCase() === normalizedStatus);
    }

    private renderComment(entry: HistoryEntry): HTMLParagraphElement | null {
        const comment = this.getComment(entry);

        if (!comment) {
            return null;
        }

        const commentElement = document.createElement('p');
        commentElement.className = 'quote-detail-history__comment';
        commentElement.textContent = comment;

        return commentElement;
    }

    private renderActionFallback(entry: HistoryEntry): HTMLElement {
        const action = this.formatValue(entry.action) || this.getSnippet('historyChangeFallback');

        return this.renderChangesContainer([
            this.renderSummaryChangeItem(null, action),
        ]);
    }

    private getComment(entry: HistoryEntry): string {
        if (typeof entry.comment === 'string') {
            return entry.comment;
        }

        const comment = asRecord(entry.comment);

        return this.formatValue(comment.comment ?? comment.text ?? comment.content);
    }

    private getActor(entry: HistoryEntry): HistoryActor {
        const employee = asRecord(entry.employee);
        const customer = asRecord(entry.customer);
        const createdBy = asRecord(entry.createdBy);
        const hasEmployee = Object.keys(employee).length > 0;
        const hasCustomer = Object.keys(customer).length > 0;
        const actor = hasEmployee
            ? employee
            : hasCustomer
                ? customer
                : createdBy;
        const fallback = hasCustomer || entry.customerId
            ? this.getSnippet('customerComment')
            : this.getSnippet('merchantComment');
        const name = this.getName(actor) || fallback;

        return {
            name,
            initials: this.getInitials(name),
            isCustomer: hasCustomer || Boolean(entry.customerId),
        };
    }

    private getHistoryDate(entry: HistoryEntry): unknown {
        if (this.isCustomerOrEmployeeHistory(entry)) {
            return entry.createdAt ?? entry.sentAt;
        }

        return entry.sentAt ?? entry.createdAt;
    }

    private getMerchantMergeTimestamp(entry: HistoryEntry): number | null {
        return this.getTimestamp(entry.sentAt);
    }

    private getCreatedAtMergeTimestamp(entry: HistoryEntry): number | null {
        return this.getTimestamp(entry.createdAt);
    }

    private getTimestamp(value: unknown): number | null {
        const dateValue = this.formatValue(value);

        if (!dateValue) {
            return null;
        }

        const timestamp = new Date(dateValue).getTime();

        return Number.isNaN(timestamp) ? null : timestamp;
    }

    private isWithinMergeWindow(timestamp: number | null, targetTimestamp: number | null): boolean {
        return timestamp !== null
            && targetTimestamp !== null
            && Math.abs(timestamp - targetTimestamp) <= HISTORY_MERGE_WINDOW_MS;
    }

    private isCustomerOrEmployeeHistory(entry: HistoryEntry): boolean {
        const customer = asRecord(entry.customer);
        const employee = asRecord(entry.employee);

        return Boolean(entry.customerId || entry.employeeId || Object.keys(customer).length || Object.keys(employee).length);
    }

    private getName(person: Record<string, unknown>): string {
        const firstName = this.formatValue(person.firstName);
        const lastName = this.formatValue(person.lastName);
        const name = [firstName, lastName].filter(Boolean).join(' ');

        return name || this.formatValue(person.name);
    }

    private getInitials(name: string): string {
        return name
            .split(' ')
            .filter(Boolean)
            .slice(0, 2)
            .map((part) => part.charAt(0))
            .join('')
            .toUpperCase();
    }

    private formatDate(value: unknown): string {
        const dateValue = this.formatValue(value);

        if (!dateValue) {
            return '';
        }

        const date = new Date(dateValue);

        if (Number.isNaN(date.getTime())) {
            return dateValue;
        }

        const formatterOptions: Intl.DateTimeFormatOptions = {
            dateStyle: 'short',
            timeStyle: 'short',
        };
        if (this.renderOptions.dateTimeTimeZone) {
            formatterOptions.timeZone = this.renderOptions.dateTimeTimeZone;
        }
        return new Intl.DateTimeFormat(
            this.renderOptions.dateTimeLocale || document.documentElement.lang || undefined,
            formatterOptions,
        ).format(date);
    }

    private formatLabel(value: string): string {
        return value
            .replace(/([A-Z])/g, ' $1')
            .replace(/[_-]/g, ' ')
            .replace(/\s+/g, ' ')
            .trim()
            .replace(/^./, (letter) => letter.toUpperCase()) || this.getSnippet('historyChangeFallback');
    }

    private formatStatusLabel(value: string): string {
        return this.formatLabel(value);
    }

    private getStatusLabel(state: string): string {
        const label = this.renderOptions.statusLabels?.[state];

        if (label && label !== `quoteManagement.details.${state}`) {
            return label;
        }

        return this.formatStatusLabel(state);
    }

    private formatChangeValue(field: string, value: unknown): string {
        if (['storefrontState', 'lineItems'].includes(field)) {
            const formattedValue = this.formatValue(value);

            return formattedValue ? this.formatStatusLabel(formattedValue) : '';
        }

        if (field === 'discount') {
            return this.formatDiscount(value);
        }

        const record = asRecord(value);
        const price = this.formatCurrency(record.totalPrice);

        return price || this.formatValue(value);
    }

    private formatConfiguredValue(value: unknown, formatter?: QuoteSummaryItem['formatter']): string {
        if (formatter === 'currency') {
            return this.formatCurrency(value);
        }

        if (formatter === 'discount') {
            return this.formatDiscount(value);
        }

        if (formatter === 'datetime') {
            return this.formatDateTimeValue(value);
        }

        if (formatter === 'label') {
            const formattedValue = this.formatValue(value);

            return formattedValue ? this.formatStatusLabel(formattedValue) : '';
        }

        return this.formatValue(value);
    }

    private formatDateTimeValue(value: unknown): string {
        const dateValue = this.formatValue(value);

        if (!dateValue) {
            return '';
        }

        const date = new Date(dateValue.replace(' ', 'T'));

        if (Number.isNaN(date.getTime())) {
            return dateValue;
        }

        const formatterOptions: Intl.DateTimeFormatOptions = {
            year: '2-digit',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
        };

        if (this.renderOptions.dateTimeTimeZone) {
            formatterOptions.timeZone = this.renderOptions.dateTimeTimeZone;
        }

        const parts = new Intl.DateTimeFormat(
            this.renderOptions.dateTimeLocale || document.documentElement.lang || undefined,
            formatterOptions,
        ).formatToParts(date);
        const values = parts.reduce<Record<string, string>>((accumulator, part) => {
            accumulator[part.type] = part.value;

            return accumulator;
        }, {});

        return `${values.day}/${values.month}/${values.year} ${values.hour}:${values.minute} ${values.dayPeriod?.toLowerCase() ?? ''}`.trim();
    }

    private formatDiscount(value: unknown): string {
        const record = asRecord(value);
        const type = this.formatValue(record.type);
        const discountValue = record.value;

        if (!type || discountValue === null || discountValue === undefined) {
            return this.formatValue(value);
        }

        if (type === 'absolute') {
            return this.getSnippet('historyDiscountAbsolute').replace('%value%', this.formatCurrency(Number(discountValue)));
        }

        return this.getSnippet('historyDiscountRelative').replace('%value%', this.formatValue(discountValue));
    }

    private formatCurrency(value: unknown): string {
        if (typeof value !== 'number') {
            return '';
        }

        try {
            return new Intl.NumberFormat(document.documentElement.lang || undefined, {
                style: 'currency',
                currency: this.renderOptions.currencyIsoCode || 'EUR',
            }).format(value);
        } catch {
            return this.formatValue(value);
        }
    }

    private formatValue(value: unknown): string {
        if (value === null || value === undefined) {
            return '';
        }

        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
            return String(value);
        }

        const record = asRecord(value);
        const translated = asRecord(record.translated);

        return this.formatValue(translated.name ?? translated.label ?? record.name ?? record.label ?? record.value);
    }

    private getValueByPath(source: HistoryEntry, path: string): unknown {
        return path.split('.').reduce<unknown>((value, key) => {
            const record = asRecord(value);

            return record[key];
        }, source);
    }

    private getQuoteSummaryStatusChangeValue(entry: HistoryEntry): string {
        const quoteSummaryItem = this.renderOptions.quoteSummary?.items?.[0];
        const configuredValue = quoteSummaryItem ? this.resolveSummaryItemValue(quoteSummaryItem, entry) : '';
        const quoteId = this.formatValue(entry.quoteId);

        return configuredValue || this.formatQuoteSummaryValue(quoteId);
    }

    private formatQuoteSummaryValue(quoteNumber: string): string {
        if (!quoteNumber) {
            return '';
        }

        return this.getSnippet('historySentQuoteValue').replace('%quoteNumber%', quoteNumber);
    }

    private getQuoteSummaryStatusChangeLabel(entry: HistoryEntry): string {
        const newStatus = this.getStatusChange(entry);

        if (this.isWithdrawStatus(newStatus)) {
            return this.renderOptions.quoteSummary?.statusChangeLabels?.withdraw
                ?? this.getStatusLabel('withdrawn');
        }

        if (this.isDraftToRequestSentStatusChange(entry)) {
            return this.renderOptions.quoteSummary?.statusChangeLabels?.added
                ?? 'Added';
        }

        return this.renderOptions.quoteSummary?.statusChangeLabels?.sent
            ?? this.renderOptions.quoteSummary?.items?.[0]?.label
            ?? '';
    }

    private getSummaryItemByChangeField(field: string): QuoteSummaryItem | undefined {
        return this.renderOptions.quoteSummary?.items?.find((item) => {
            return item.valuePath && this.getChangeFieldFromValuePath(item.valuePath) === field;
        });
    }

    private getChangeFieldFromValuePath(valuePath: string): string {
        const prefix = 'changes.new.';

        if (!valuePath.startsWith(prefix)) {
            return '';
        }

        return valuePath.slice(prefix.length).split('.')[0] ?? '';
    }

    private isWithdrawStatus(status: string): boolean {
        const normalizedStatus = status.toLowerCase();

        return normalizedStatus === 'withdrawn' || normalizedStatus === 'withdraw';
    }

    private cloneTemplate(selector: string): HTMLElement | null {
        const template = this.el.querySelector<HTMLTemplateElement>(selector);
        const element = template?.content.firstElementChild?.cloneNode(true);

        return element instanceof HTMLElement ? element : null;
    }

    private getSnippet(key: keyof Snippets): string {
        return this.renderOptions.snippets?.[key] ?? DEFAULT_SNIPPETS[key];
    }

    private getMerchantActor(): HistoryActor {
        const name = this.getSnippet('merchantComment');

        return {
            name,
            initials: this.getInitials(name),
            isCustomer: false,
        };
    }
}
